import React, { useState, useEffect, useCallback } from "react";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconRotate,
  IconList,
  IconTrash,
  IconEdit,
  IconAward,
  IconPuzzle,
  IconTrophy,
  IconCoffee,
  IconBook,
  IconClock,
  IconThumbUp,
  IconBulb,
  IconX,
  IconCheck,
  IconUpload,
  IconDownload,
  IconDotsVertical,
  IconEye,
  IconRefresh,
  IconFileText,
} from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import InputField from "../../../components/common/InputField";
import TextareaField from "../../../components/common/TextareaField";
import {
  fetchHrAwards,
  createHrAward,
  updateHrAward,
  deleteHrAward,
  fetchHrAppreciations,
  createHrAppreciation,
  updateHrAppreciation,
  deleteHrAppreciation,
  fetchHrCompany,
  fetchHrBranches,
  fetchHrEmployees,
} from "../../../apis/hrApi";
import toast from "react-hot-toast";

// Emojis mapping for database payload <-> UI icons
const EMOJI_TO_KEY: Record<string, string> = {
  "🏆": "trophy",
  "🎖️": "award",
  "🧩": "puzzle",
  "☕": "coffee",
  "📖": "book",
  "⏰": "clock",
  "👍": "thumbup",
  "💡": "bulb"
};

const KEY_TO_EMOJI: Record<string, string> = {
  "trophy": "🏆",
  "award": "🎖️",
  "puzzle": "🧩",
  "coffee": "☕",
  "book": "📖",
  "clock": "⏰",
  "thumbup": "👍",
  "bulb": "💡"
};

// Available icons to select for new Award categories
const ICON_OPTIONS = [
  { key: "award", component: IconAward, colorClass: "bg-amber-100 text-amber-600" },
  { key: "puzzle", component: IconPuzzle, colorClass: "bg-emerald-100 text-emerald-600" },
  { key: "trophy", component: IconTrophy, colorClass: "bg-pink-100 text-pink-600" },
  { key: "coffee", component: IconCoffee, colorClass: "bg-green-100 text-green-600" },
  { key: "book", component: IconBook, colorClass: "bg-purple-100 text-purple-600" },
  { key: "clock", component: IconClock, colorClass: "bg-orange-100 text-orange-600" },
  { key: "thumbup", component: IconThumbUp, colorClass: "bg-blue-100 text-blue-600" },
  { key: "bulb", component: IconBulb, colorClass: "bg-teal-100 text-teal-600" }
];

const MOCK_EMPLOYEES = [
  { id: "emp-1", name: "Roshan", dept: "Human Resources", designation: "HR Specialist" },
  { id: "emp-2", name: "Yash Koladiya", dept: "Human Resources", designation: "HR Manager" },
  { id: "emp-3", name: "Akash", dept: "IT & Support", designation: "Support Lead" },
  { id: "emp-4", name: "Himmat", dept: "Customer Service Department", designation: "Support Agent" },
  { id: "emp-5", name: "Laksh", dept: "Analytics Department", designation: "Data Analyst" },
  { id: "emp-6", name: "Ramesh", dept: "Quality Control Department", designation: "QC Inspector" }
];

const MOCK_AWARDS = [
  { id: "mock-award-1", title: "Best Team Player", category: "Performance", iconKey: "thumbup", color: "#FFD700", rewardAllowed: true, active: true, createdAt: "14-07-2026 04:30 PM" },
  { id: "mock-award-2", title: "Innovation Champion", category: "Performance", iconKey: "bulb", color: "#4CAF50", rewardAllowed: true, active: true, createdAt: "14-07-2026 04:28 PM" },
  { id: "mock-award-3", title: "Discipline", category: "Behavioral", iconKey: "puzzle", color: "#2196F3", rewardAllowed: false, active: true, createdAt: "14-07-2026 04:10 PM" },
  { id: "mock-award-4", title: "Project Excellence Award", category: "Performance", iconKey: "trophy", color: "#E91E63", rewardAllowed: true, active: true, createdAt: "14-07-2026 03:42 PM" },
  { id: "mock-award-5", title: "Sustainability Advocate", category: "Performance", iconKey: "coffee", color: "#FF9800", rewardAllowed: true, active: true, createdAt: "14-07-2026 03:39 PM" }
];

const MOCK_APPRECIATIONS = [
  {
    id: "app-1",
    name: "Roshan",
    dept: "Human Resources",
    award: "Best Team Player",
    givenOn: "14-07-2026",
    createdAt: "14-07-2026 04:30 PM",
    updatedAt: "14-07-2026 04:30 PM",
    rawEmployeeId: "emp-1",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-1",
    rawRecognitionDate: "2026-07-14",
    rawVisibility: "PUBLIC",
    rawSummary: "Consistently demonstrates great teamwork and supports peers.",
    rawAttachments: []
  },
  {
    id: "app-2",
    name: "Roshan",
    dept: "Human Resources",
    award: "Best Team Player",
    givenOn: "14-07-2026",
    createdAt: "14-07-2026 04:30 PM",
    updatedAt: "14-07-2026 04:30 PM",
    rawEmployeeId: "emp-1",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-1",
    rawRecognitionDate: "2026-07-14",
    rawVisibility: "PUBLIC",
    rawSummary: "Goes above and beyond to collaborate with other teams.",
    rawAttachments: []
  },
  {
    id: "app-3",
    name: "Akash",
    dept: "IT & Support",
    award: "Innovation Champion",
    givenOn: "15-07-2026",
    createdAt: "14-07-2026 04:28 PM",
    updatedAt: "14-07-2026 04:28 PM",
    rawEmployeeId: "emp-3",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-2",
    rawRecognitionDate: "2026-07-15",
    rawVisibility: "PUBLIC",
    rawSummary: "Proposed a novel automation script that saves hours of support work.",
    rawAttachments: []
  },
  {
    id: "app-4",
    name: "Akash",
    dept: "IT & Support",
    award: "Innovation Champion",
    givenOn: "15-07-2026",
    createdAt: "14-07-2026 04:28 PM",
    updatedAt: "14-07-2026 04:28 PM",
    rawEmployeeId: "emp-3",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-2",
    rawRecognitionDate: "2026-07-15",
    rawVisibility: "PUBLIC",
    rawSummary: "Helped redesign key network modules for higher throughput.",
    rawAttachments: []
  },
  {
    id: "app-5",
    name: "Himmat",
    dept: "Customer Service Department",
    award: "Discipline",
    givenOn: "10-07-2026",
    createdAt: "14-07-2026 04:10 PM",
    updatedAt: "14-07-2026 04:12 PM",
    rawEmployeeId: "emp-4",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-3",
    rawRecognitionDate: "2026-07-10",
    rawVisibility: "PUBLIC",
    rawSummary: "Maintains exemplary attendance and punctuality records.",
    rawAttachments: []
  },
  {
    id: "app-6",
    name: "Roshan",
    dept: "Human Resources",
    award: "Best Team Player",
    givenOn: "14-07-2026",
    createdAt: "14-07-2026 03:44 PM",
    updatedAt: "14-07-2026 04:08 PM",
    rawEmployeeId: "emp-1",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-1",
    rawRecognitionDate: "2026-07-14",
    rawVisibility: "PUBLIC",
    rawSummary: "Outstanding performance during company recruitment drive.",
    rawAttachments: []
  },
  {
    id: "app-7",
    name: "Himmat",
    dept: "Customer Service Department",
    award: "Project Excellence Award",
    givenOn: "14-07-2026",
    createdAt: "14-07-2026 03:42 PM",
    updatedAt: "14-07-2026 03:42 PM",
    rawEmployeeId: "emp-4",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-4",
    rawRecognitionDate: "2026-07-14",
    rawVisibility: "PUBLIC",
    rawSummary: "Successfully delivered key customer service modules ahead of schedule.",
    rawAttachments: []
  },
  {
    id: "app-8",
    name: "Himmat",
    dept: "Customer Service Department",
    award: "Sustainability Advocate",
    givenOn: "13-07-2026",
    createdAt: "14-07-2026 03:39 PM",
    updatedAt: "14-07-2026 04:25 PM",
    rawEmployeeId: "emp-4",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-5",
    rawRecognitionDate: "2026-07-13",
    rawVisibility: "PUBLIC",
    rawSummary: "Initiated a green paperless workflow for support documentation.",
    rawAttachments: []
  },
  {
    id: "app-9",
    name: "Laksh",
    dept: "Analytics Department",
    award: "Project Excellence Award",
    givenOn: "13-07-2026",
    createdAt: "14-07-2026 03:31 PM",
    updatedAt: "14-07-2026 04:28 PM",
    rawEmployeeId: "emp-5",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-4",
    rawRecognitionDate: "2026-07-13",
    rawVisibility: "PUBLIC",
    rawSummary: "Delivered highly optimized regression reports for strategic planning.",
    rawAttachments: []
  },
  {
    id: "app-10",
    name: "Ramesh",
    dept: "Quality Control Department",
    award: "Best Team Player",
    givenOn: "14-07-2026",
    createdAt: "14-07-2026 02:59 PM",
    updatedAt: "14-07-2026 02:59 PM",
    rawEmployeeId: "emp-6",
    rawRecognizedById: "emp-2",
    rawAwardId: "mock-award-1",
    rawRecognitionDate: "2026-07-14",
    rawVisibility: "PUBLIC",
    rawSummary: "Always helpful and cooperative during product stress test runs.",
    rawAttachments: []
  }
];

const SectionBlock = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm p-4 transition-all duration-300 space-y-4 relative">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
    <div className="flex items-center gap-2 border-[#FBD037]/30 pb-2 mb-1.5 relative z-10 border-b">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
    <div className="relative z-10 space-y-4">
      {children}
    </div>
  </div>
);

export default function Appreciation() {
  const [activeTab, setActiveTab] = useState<"appreciation" | "awards">("appreciation");

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data list states
  const [appreciations, setAppreciations] = useState<any[]>([]);
  const [awards, setAwards] = useState<any[]>([]);
  const [allAwards, setAllAwards] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);

  // Metadata ids from backend
  const [companyId, setCompanyId] = useState("");
  const [branchId, setBranchId] = useState("");

  // Selection states
  const [selectedAppreciations, setSelectedAppreciations] = useState<any[]>([]);
  const [selectedAwards, setSelectedAwards] = useState<any[]>([]);

  // New appreciation form inputs
  const [newAppEmployee, setNewAppEmployee] = useState("");
  const [newAppAward, setNewAppAward] = useState("");
  const [newAppRecognizedBy, setNewAppRecognizedBy] = useState("");
  const [newAppGivenOn, setNewAppGivenOn] = useState(new Date().toISOString().split("T")[0]);
  const [newAppVisibility, setNewAppVisibility] = useState("PUBLIC");
  const [newAppSummary, setNewAppSummary] = useState("");
  const [newAppFile, setNewAppFile] = useState<File | null>(null);
  const [newAppFileName, setNewAppFileName] = useState("");

  // Sidebar States (for Add, View, Edit, Delete Award or Appreciation sidebar card)
  const [sidebarMode, setSidebarMode] = useState<"ADD" | "VIEW" | "EDIT" | "DELETE" | "IMPORT" | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [activeActionMenuId, setActiveActionMenuId] = useState<string | null>(null);

  // Form fields states for Award
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editIconKey, setEditIconKey] = useState("award");
  const [editColor, setEditColor] = useState("#FFD700");
  const [editRewardAllowed, setEditRewardAllowed] = useState(true);
  const [editSummary, setEditSummary] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal states for Create Award Type from Assign Appreciation form
  const [showCreateAwardModal, setShowCreateAwardModal] = useState(false);
  const [modalAwardTitle, setModalAwardTitle] = useState("");
  const [modalAwardCategory, setModalAwardCategory] = useState("");
  const [modalAwardIconKey, setModalAwardIconKey] = useState("");
  const [modalAwardColor, setModalAwardColor] = useState("#ff0000");
  const [modalAwardRewardAllowed, setModalAwardRewardAllowed] = useState(false);
  const [modalAwardSummary, setModalAwardSummary] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalAwardsPages, setTotalAwardsPages] = useState(1);
  const [totalAwardsRecords, setTotalAwardsRecords] = useState(0);
  const [totalAppreciationsPages, setTotalAppreciationsPages] = useState(1);
  const [totalAppreciationsRecords, setTotalAppreciationsRecords] = useState(0);
  const itemsPerPage = 10;

  const loadAllAwards = useCallback(async () => {
    try {
      const res = await fetchHrAwards(1, 1000, "");
      let list: any[] = [];
      if (res.success) {
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.awards || res.data.list || res.data.data;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
      }
      if (!res.success || list.length === 0) {
        list = MOCK_AWARDS;
      }
      const mapped = list.map((a: any) => {
        const matchedEmoji = a.icon || "🏆";
        const iconKey = EMOJI_TO_KEY[matchedEmoji] || a.iconKey || "award";
        return {
          ...a,
          id: a.id || a._id,
          title: a.title || "",
          iconKey: iconKey,
          summary: a.summary || "",
          category: a.category || "Performance",
          color: a.color || "#FFD700",
          rewardAllowed: !!a.rewardAllowed || !!a.isRewardAllowed,
          active: a.status === true || a.status === "ACTIVE" || a.active === true,
        };
      });
      setAllAwards(mapped);
      if (mapped.length > 0 && !newAppAward) {
        const activeAward = mapped.find(a => a.active);
        if (activeAward) setNewAppAward(activeAward.id);
        else setNewAppAward(mapped[0].id);
      }
    } catch (err) {
      console.error("Error loading all awards:", err);
      const mappedMock = MOCK_AWARDS.map((a: any) => ({ ...a, active: true }));
      setAllAwards(mappedMock);
      if (mappedMock.length > 0 && !newAppAward) {
        setNewAppAward(mappedMock[0].id);
      }
    }
  }, [newAppAward]);

  // Load HR Company/Branch/Employees metadata
  const loadMetadata = useCallback(async () => {
    try {
      loadAllAwards();
      const empRes = await fetchHrEmployees(1, 1000);
      let mappedEmp: any[] = [];
      if (empRes.success && empRes.data?.employees && empRes.data.employees.length > 0) {
        mappedEmp = empRes.data.employees.map((emp: any) => ({
          id: emp.id || emp._id,
          name: emp.firstName ? `${emp.firstName} ${emp.lastName || ""}`.trim() : emp.name || "Unknown",
          dept: emp.departmentName || emp.department || "-",
          designation: emp.designationName || emp.designation || "-"
        }));
      } else {
        mappedEmp = MOCK_EMPLOYEES;
      }
      setEmployees(mappedEmp);
      if (mappedEmp.length > 0) {
        setNewAppEmployee(mappedEmp[0].id);
        setNewAppRecognizedBy(mappedEmp[0].id);
      }

      const compRes = await fetchHrCompany();
      if (compRes.success && compRes.data) {
        setCompanyId(compRes.data.id || compRes.data._id || "");
      }

      const branchRes = await fetchHrBranches(1, 10);
      if (branchRes.success && branchRes.data?.branches?.length > 0) {
        setBranchId(branchRes.data.branches[0].id || branchRes.data.branches[0]._id || "");
      }
    } catch (err) {
      console.error("Error loading HR metadata, falling back to mock employees:", err);
      setEmployees(MOCK_EMPLOYEES);
      setNewAppEmployee(MOCK_EMPLOYEES[0].id);
      setNewAppRecognizedBy(MOCK_EMPLOYEES[0].id);
    }
  }, [loadAllAwards]);

  // Load Awards categories from API
  const loadAwards = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchHrAwards(page, itemsPerPage, searchQuery);
      let list: any[] = [];
      let total = 0;
      let totalPages = 1;
      if (res.success) {
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.awards || res.data.list || res.data.data;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        total = res.data?.total || res.data?.totalCount || list.length;
        totalPages = res.data?.totalPages || Math.ceil(total / itemsPerPage) || 1;
      }

      if (!res.success || list.length === 0) {
        list = MOCK_AWARDS;
        total = MOCK_AWARDS.length;
        totalPages = 1;
      }
      
      const mapped = list.map((a: any) => {
        const matchedEmoji = a.icon || "🏆";
        const iconKey = EMOJI_TO_KEY[matchedEmoji] || a.iconKey || "award";
        const formattedCreatedAt = a.createdAt
          ? (String(a.createdAt).includes("PM") || String(a.createdAt).includes("AM")
              ? a.createdAt
              : new Date(a.createdAt).toLocaleDateString("en-GB").replace(/\//g, "-") + " " + new Date(a.createdAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }))
          : "-";
        return {
          ...a,
          id: a.id || a._id,
          title: a.title || "",
          iconKey: iconKey,
          summary: a.summary || "",
          category: a.category || "Performance",
          color: a.color || "#FFD700",
          rewardAllowed: !!a.rewardAllowed || !!a.isRewardAllowed,
          active: a.status === true || a.status === "ACTIVE" || a.active === true,
          createdAt: formattedCreatedAt
        };
      });
      setAwards(mapped);
      
      if (mapped.length > 0 && !newAppAward) {
        const activeAward = mapped.find(a => a.active);
        if (activeAward) setNewAppAward(activeAward.id);
        else setNewAppAward(mapped[0].id);
      }

      setTotalAwardsRecords(total);
      setTotalAwardsPages(totalPages);
    } catch (err) {
      console.error("Error loading awards, falling back to mock awards:", err);
      const mappedMock = MOCK_AWARDS.map((a: any) => ({
        ...a,
        active: true
      }));
      setAwards(mappedMock);
      setTotalAwardsRecords(MOCK_AWARDS.length);
      setTotalAwardsPages(1);
      if (mappedMock.length > 0 && !newAppAward) {
        setNewAppAward(mappedMock[0].id);
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, itemsPerPage, searchQuery, newAppAward]);

  // Load Appreciation records from API
  const loadAppreciations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchHrAppreciations(page, itemsPerPage, searchQuery, companyId);
      let list: any[] = [];
      let total = 0;
      let totalPages = 1;
      if (res.success) {
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.appreciations || res.data.list || res.data.data;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        total = res.data?.total || res.data?.totalCount || list.length;
        totalPages = res.data?.totalPages || Math.ceil(total / itemsPerPage) || 1;
      }

      if (!res.success || list.length === 0) {
        list = MOCK_APPRECIATIONS;
        total = MOCK_APPRECIATIONS.length;
        totalPages = 1;
      }

      const mapped = list.map((item: any) => {
        const empName = (item.employeeFirstName || item.employeeLastName)
          ? `${item.employeeFirstName || ""} ${item.employeeLastName || ""}`.trim()
          : item.employee
            ? `${item.employee.firstName || ""} ${item.employee.lastName || ""}`.trim()
            : item.name || item.employeeName || "Unknown";
        const empDept = item.employee?.departmentName || item.employee?.department?.departmentName || item.dept || "-";

        const recogByName = (item.recognizedByFirstName || item.recognizedByLastName)
          ? `${item.recognizedByFirstName || ""} ${item.recognizedByLastName || ""}`.trim()
          : item.recognizedBy
            ? `${item.recognizedBy.firstName || ""} ${item.recognizedBy.lastName || ""}`.trim()
            : item.recognizedByName || "Unknown";
        const recogByDept = item.recognizedBy?.designationName || item.recognizedBy?.departmentName || "-";

        const awardName = item.awardTitle || (item.award ? (typeof item.award === "object" ? item.award.title : item.award) : item.awardName || "Unknown Award");

        const givenDate = item.recognitionDate 
          ? (item.recognitionDate.includes("-") && !item.recognitionDate.includes("T")
              ? item.recognitionDate.split("-").reverse().join("-")
              : new Date(item.recognitionDate).toLocaleDateString("en-GB").replace(/\//g, "-"))
          : item.givenOn || "-";

        const formattedCreated = item.createdAt
          ? (item.createdAt.includes("PM") || item.createdAt.includes("AM")
              ? item.createdAt
              : new Date(item.createdAt).toLocaleDateString("en-GB").replace(/\//g, "-") + " " + new Date(item.createdAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }))
          : "-";

        const formattedUpdated = item.updatedAt
          ? (item.updatedAt.includes("PM") || item.updatedAt.includes("AM")
              ? item.updatedAt
              : new Date(item.updatedAt).toLocaleDateString("en-GB").replace(/\//g, "-") + " " + new Date(item.updatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }))
          : "-";

        return {
          ...item,
          id: item.id || item._id,
          name: empName,
          dept: empDept,
          award: awardName,
          givenOn: givenDate,
          createdAt: formattedCreated,
          updatedAt: formattedUpdated,
          rawEmployeeId: item.employee?.id || item.employee?._id || item.employeeId || item.rawEmployeeId || "",
          rawRecognizedById: item.recognizedBy?.id || item.recognizedBy?._id || item.recognizedById || item.rawRecognizedById || "",
          rawAwardId: item.award?.id || item.award?._id || item.awardId || item.rawAwardId || "",
          rawRecognitionDate: item.recognitionDate ? item.recognitionDate.split("T")[0] : item.rawRecognitionDate || "",
          rawVisibility: item.visibility || item.rawVisibility || "PUBLIC",
          rawSummary: item.summary || item.rawSummary || "",
          rawAttachments: item.attachments || item.rawAttachments || []
        };
      });

      setAppreciations(mapped);
      setTotalAppreciationsRecords(total);
      setTotalAppreciationsPages(totalPages);
    } catch (err) {
      console.error("Error loading appreciations, falling back to mock appreciations:", err);
      setAppreciations(MOCK_APPRECIATIONS);
      setTotalAppreciationsRecords(MOCK_APPRECIATIONS.length);
      setTotalAppreciationsPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [page, itemsPerPage, searchQuery, companyId]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    if (activeTab === "appreciation") {
      loadAppreciations();
    } else {
      loadAwards();
    }
  }, [activeTab, loadAppreciations, loadAwards]);

  // Helper to render Avatar based on initials
  const renderAvatar = (name: string) => {
    const safeName = typeof name === "string" ? name : "Unknown";
    const initials = safeName
      .split(" ")
      .filter(Boolean)
      .map(n => n[0])
      .join("")
      .toUpperCase();
    const colors = [
      "bg-indigo-100 text-indigo-700 border-indigo-200",
      "bg-emerald-100 text-emerald-700 border-emerald-200",
      "bg-blue-100 text-blue-700 border-blue-200",
      "bg-purple-100 text-purple-700 border-purple-200",
      "bg-amber-100 text-amber-700 border-amber-200",
      "bg-pink-100 text-pink-700 border-pink-200",
      "bg-teal-100 text-teal-700 border-teal-200"
    ];
    const colorClass = colors[safeName.length % colors.length];
    return (
      <div className={`w-8 h-8 rounded-full ${colorClass} border flex items-center justify-center font-bold text-xs shrink-0`}>
        {initials}
      </div>
    );
  };

  // Helper to fetch details for configured Awards
  const getAwardIconConfig = (awardName: string) => {
    const safeAwardName = typeof awardName === "string" ? awardName.toLowerCase() : "";
    const matchedCategory = awards.find(a => {
      const titleStr = typeof a?.title === "string" ? a.title.toLowerCase() : "";
      return titleStr && titleStr === safeAwardName;
    });
    const matchedIconKey = matchedCategory ? matchedCategory.iconKey : "award";
    const opt = ICON_OPTIONS.find(o => o.key === matchedIconKey) || ICON_OPTIONS[0];
    return opt;
  };

  // Switch status toggle for award category
  const handleToggleAwardStatus = async (item: any) => {
    try {
      const newStatus = !item.active;
      // Map iconKey back to emoji
      const emojiIcon = KEY_TO_EMOJI[item.iconKey] || "🏆";

      const payload = {
        title: item.title,
        category: item.category || "Performance",
        icon: emojiIcon,
        color: item.color || "#FFD700",
        isRewardAllowed: !!item.rewardAllowed,
        summary: item.summary || "",
        status: newStatus ? "ACTIVE" : "INACTIVE"
      };
      
      const res = await updateHrAward(item.id || item._id, payload);
      if (res.success) {
        toast.success(`Status updated successfully`);
        loadAwards();
        loadAllAwards();
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // Appreciation Save Submit Handler (Add and Edit)
  const handleAppreciationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAppEmployee) {
      toast.error("Please select an employee.");
      return;
    }
    if (!newAppAward) {
      toast.error("Please select an award type.");
      return;
    }
    if (!newAppRecognizedBy) {
      toast.error("Please select the recognizer.");
      return;
    }
    if (!newAppSummary.trim()) {
      toast.error("Please enter a summary.");
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("employeeId", newAppEmployee);
    formData.append("awardId", newAppAward);
    formData.append("recognizedById", newAppRecognizedBy);
    formData.append("recognitionDate", newAppGivenOn);
    formData.append("visibility", newAppVisibility);
    formData.append("summary", newAppSummary.trim());
    if (newAppFile) {
      formData.append("attachments", newAppFile);
    }

    try {
      if (sidebarMode === "ADD") {
        const res = await createHrAppreciation(formData);
        if (res.success) {
          toast.success("Appreciation assigned successfully!");
          loadAppreciations();
          setSidebarMode(null);
          setSelectedItem(null);
        } else {
          toast.error(res.message || "Failed to assign appreciation");
        }
      } else if (sidebarMode === "EDIT" && selectedItem) {
        const res = await updateHrAppreciation(selectedItem.id || selectedItem._id, formData);
        if (res.success) {
          toast.success("Appreciation updated successfully!");
          loadAppreciations();
          setSidebarMode(null);
          setSelectedItem(null);
        } else {
          toast.error(res.message || "Failed to update appreciation");
        }
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "An error occurred while saving appreciation");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete Handlers for Appreciations
  const handleConfirmDeleteAppreciation = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const res = await deleteHrAppreciation(selectedItem.id || selectedItem._id);
      if (res.success) {
        toast.success("Appreciation record deleted successfully!");
        loadAppreciations();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to delete appreciation record");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "An error occurred while deleting appreciation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAppreciations = async () => {
    if (selectedAppreciations.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedAppreciations.length} appreciation record(s)?`)) {
      const loader = toast.loading(`Deleting ${selectedAppreciations.length} records...`);
      try {
        for (const id of selectedAppreciations) {
          await deleteHrAppreciation(String(id));
        }
        toast.success("Selected appreciation records deleted.", { id: loader });
        setSelectedAppreciations([]);
        loadAppreciations();
      } catch (err: any) {
        toast.error(err.message || "An error occurred during bulk deletion", { id: loader });
      }
    }
  };

  const handleDeleteAwards = () => {
    if (selectedAwards.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedAwards.length} award category/categories?`)) {
      setSelectedAwards([]);
      toast.success("Operations not permitted on multiple bulk backend objects.");
    }
  };

  // Sidebar Actions Trigger (Awards)
  const handleOpenAdd = () => {
    setSelectedItem(null);
    setEditTitle("");
    setEditCategory("Performance");
    setEditIconKey("award");
    setEditColor("#FFD700");
    setEditRewardAllowed(true);
    setEditSummary("");
    setSidebarMode("ADD");
  };

  const handleOpenView = (item: any) => {
    setSelectedItem(item);
    setSidebarMode("VIEW");
  };

  const handleOpenEdit = (item: any) => {
    setSelectedItem(item);
    setEditTitle(item.title || "");
    setEditCategory(item.category || "Performance");
    setEditIconKey(item.iconKey || "award");
    setEditColor(item.color || "#FFD700");
    setEditRewardAllowed(!!item.rewardAllowed);
    setEditSummary(item.summary || "");
    setSidebarMode("EDIT");
  };

  const handleOpenDelete = (item: any) => {
    setSelectedItem(item);
    setSidebarMode("DELETE");
  };

  // Sidebar Actions Trigger (Appreciations)
  const handleOpenAddAppreciation = () => {
    setSelectedItem(null);
    setNewAppEmployee(employees[0]?.id || "");
    setNewAppAward(awards.filter(a => a.active)[0]?.id || awards[0]?.id || "");
    setNewAppRecognizedBy(employees[0]?.id || "");
    setNewAppGivenOn(new Date().toISOString().split("T")[0]);
    setNewAppVisibility("PUBLIC");
    setNewAppSummary("");
    setNewAppFile(null);
    setNewAppFileName("");
    setSidebarMode("ADD");
  };

  const handleOpenViewAppreciation = (item: any) => {
    setSelectedItem(item);
    setSidebarMode("VIEW");
  };

  const handleOpenEditAppreciation = (item: any) => {
    setSelectedItem(item);
    setNewAppEmployee(item.rawEmployeeId);
    setNewAppAward(item.rawAwardId);
    setNewAppRecognizedBy(item.rawRecognizedById);
    setNewAppGivenOn(item.rawRecognitionDate);
    setNewAppVisibility(item.rawVisibility);
    setNewAppSummary(item.rawSummary);
    setNewAppFile(null);
    setNewAppFileName("");
    setSidebarMode("EDIT");
  };

  const handleOpenDeleteAppreciation = (item: any) => {
    setSelectedItem(item);
    setSidebarMode("DELETE");
  };

  const handleOpenImportAppreciation = () => {
    setSelectedItem(null);
    setSidebarMode("IMPORT");
  };

  // Form submit handler for Award Category
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTitle.trim()) {
      toast.error("Please enter an award title.");
      return;
    }
    if (!editCategory.trim()) {
      toast.error("Please enter a category.");
      return;
    }
    if (!editSummary.trim()) {
      toast.error("Please enter a summary description.");
      return;
    }

    setIsSubmitting(true);
    
    // Map iconKey back to emoji
    const emojiIcon = KEY_TO_EMOJI[editIconKey] || "🏆";

    if (sidebarMode === "ADD") {
      const payload = {
        title: editTitle.trim(),
        category: editCategory.trim(),
        icon: emojiIcon,
        color: editColor,
        isRewardAllowed: editRewardAllowed,
        summary: editSummary.trim()
      };
      
      try {
        const res = await createHrAward(payload);
        if (res.success) {
          toast.success("Award category created successfully!");
          loadAwards();
          loadAllAwards();
          setSidebarMode(null);
          setSelectedItem(null);
        } else {
          toast.error(res.message || "Failed to create award category");
        }
      } catch (err) {
        console.error(err);
        toast.error("An error occurred while creating award category");
      } finally {
        setIsSubmitting(false);
      }
    } else if (sidebarMode === "EDIT" && selectedItem) {
      const payload = {
        title: editTitle.trim(),
        category: editCategory.trim(),
        icon: emojiIcon,
        color: editColor,
        isRewardAllowed: editRewardAllowed,
        summary: editSummary.trim(),
      };

      try {
        const res = await updateHrAward(selectedItem.id || selectedItem._id, payload);
        if (res.success) {
          toast.success("Award category updated successfully!");
          loadAwards();
          loadAllAwards();
          setSidebarMode(null);
          setSelectedItem(null);
        } else {
          toast.error(res.message || "Failed to update award category");
        }
      } catch (err) {
        console.error(err);
        toast.error("An error occurred while updating award category");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCreateAwardFromModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalAwardTitle.trim()) {
      toast.error("Please enter an award title.");
      return;
    }
    if (!modalAwardCategory.trim()) {
      toast.error("Please enter a category.");
      return;
    }
    if (!modalAwardIconKey) {
      toast.error("Please select an icon.");
      return;
    }
    if (!modalAwardSummary.trim()) {
      toast.error("Please enter a summary description.");
      return;
    }

    setIsSubmitting(true);
    
    // Map iconKey back to emoji
    const emojiIcon = KEY_TO_EMOJI[modalAwardIconKey] || "🏆";

    const payload = {
      title: modalAwardTitle.trim(),
      category: modalAwardCategory.trim(),
      icon: emojiIcon,
      color: modalAwardColor,
      isRewardAllowed: modalAwardRewardAllowed,
      summary: modalAwardSummary.trim()
    };
    
    try {
      const res = await createHrAward(payload);
      if (res.success) {
        toast.success("Award category created successfully!");
        
        // Refresh the list of awards and update state
        const resAwards = await fetchHrAwards(1, 1000, "");
        let updatedAwards: any[] = [];
        if (resAwards.success) {
          const list = Array.isArray(resAwards.data) ? resAwards.data : (resAwards.data?.awards || resAwards.data?.list || []);
          updatedAwards = list.map((a: any) => {
            const matchedEmoji = a.icon || "🏆";
            const iconKey = EMOJI_TO_KEY[matchedEmoji] || a.iconKey || "award";
            return {
              ...a,
              id: a.id || a._id,
              title: a.title || "",
              iconKey: iconKey,
              summary: a.summary || "",
              category: a.category || "Performance",
              color: a.color || "#FFD700",
              rewardAllowed: !!a.rewardAllowed || !!a.isRewardAllowed,
              active: a.status === true || a.status === "ACTIVE" || a.active === true,
            };
          });
          setAwards(updatedAwards);
          setAllAwards(updatedAwards);
        }

        const createdId = res.data?.id || res.data?._id;
        if (createdId) {
          setNewAppAward(createdId);
        } else {
          const matched = updatedAwards.find((a: any) => a.title.toLowerCase() === modalAwardTitle.trim().toLowerCase());
          if (matched) {
            setNewAppAward(matched.id);
          }
        }

        setShowCreateAwardModal(false);
      } else {
        toast.error(res.message || "Failed to create award category");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while creating award category");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDeleteAward = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const res = await deleteHrAward(selectedItem.id || selectedItem._id);
      if (res.success) {
        toast.success("Award category deleted successfully!");
        loadAwards();
        loadAllAwards();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to delete award category");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while deleting award category");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Paginated outputs
  const paginatedAppreciations = appreciations; // API returns paginated array directly
  const paginatedAwards = awards; // API returns paginated array directly

  const displayTotalPages = activeTab === "appreciation"
    ? totalAppreciationsPages
    : totalAwardsPages;

  const displayTotalRecords = activeTab === "appreciation"
    ? totalAppreciationsRecords
    : totalAwardsRecords;

  const appreciationColumns = [
    {
      label: (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
              checked={paginatedAppreciations.length > 0 && paginatedAppreciations.every(item => selectedAppreciations.includes(item.id))}
              onChange={e => {
                if (e.target.checked) {
                  const toAdd = paginatedAppreciations.map(i => i.id);
                  setSelectedAppreciations(prev => Array.from(new Set([...prev, ...toAdd])));
                } else {
                  const toRemove = paginatedAppreciations.map(i => i.id);
                  setSelectedAppreciations(prev => prev.filter(id => !toRemove.includes(id)));
                }
              }}
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
      render: (item: any) => (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
              checked={selectedAppreciations.includes(item.id)}
              onChange={e => {
                if (e.target.checked) {
                  setSelectedAppreciations(prev => [...prev, item.id]);
                } else {
                  setSelectedAppreciations(prev => prev.filter(id => id !== item.id));
                }
              }}
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      )
    },
    {
      label: "SR.NO",
      render: (_: any, idx: number) => (
        <span className="text-gray-400 font-bold font-mono">
          {(page - 1) * itemsPerPage + idx + 1}
        </span>
      )
    },
    {
      label: "NAME",
      render: (item: any) => (
        <div className="flex items-center gap-3 text-left">
          {renderAvatar(item.name)}
          <div>
            <div className="font-bold text-gray-800 leading-tight">{item.name}</div>
            <div className="text-[10px] text-gray-400 font-medium">{item.dept}</div>
          </div>
        </div>
      )
    },
    {
      label: "AWARD",
      render: (item: any) => {
        const awardConfig = getAwardIconConfig(item.award);
        const CustomIcon = awardConfig.component;
        return (
          <div className="flex items-center gap-2 justify-center">
            <div className={`w-7 h-7 rounded-lg ${awardConfig.colorClass} flex items-center justify-center shadow-xs shrink-0`}>
              <CustomIcon size={15} />
            </div>
            <span className="font-semibold text-gray-800">{item.award}</span>
          </div>
        );
      }
    },
    {
      label: "GIVEN ON",
      render: (item: any) => (
        <span className="text-gray-500 font-bold font-mono">{item.givenOn}</span>
      )
    },
    {
      label: "CREATED AT",
      render: (item: any) => (
        <span className="text-gray-500 font-medium font-mono">{item.createdAt}</span>
      )
    },
    {
      label: "UPDATED AT",
      render: (item: any) => (
        <span className="text-gray-500 font-medium font-mono">{item.updatedAt}</span>
      )
    },
    {
      label: "ACTIONS",
      render: (item: any) => {
        const isOpen = activeActionMenuId === item.id;
        return (
          <div className={`relative flex justify-center ${isOpen ? "z-30" : "z-10"}`}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveActionMenuId(isOpen ? null : item.id);
              }}
              className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
              title="Actions"
            >
              <IconDotsVertical size={14} stroke={1.5} />
            </button>
            
            {isOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40 bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveActionMenuId(null);
                  }}
                />
                <div className="absolute right-0 top-8 z-50 min-w-[120px] bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 text-left font-sans text-xs font-semibold">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenuId(null);
                      handleOpenViewAppreciation(item);
                    }}
                    className="w-full px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-slate-700 cursor-pointer text-left border-none bg-transparent"
                  >
                    <IconEye size={13} className="text-gray-400" />
                    View
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenuId(null);
                      handleOpenEditAppreciation(item);
                    }}
                    className="w-full px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-slate-700 cursor-pointer text-left border-none bg-transparent"
                  >
                    <IconEdit size={13} className="text-gray-400" />
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenuId(null);
                      handleOpenDeleteAppreciation(item);
                    }}
                    className="w-full px-4 py-2 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-2 text-red-500 cursor-pointer text-left border-none bg-transparent"
                  >
                    <IconTrash size={13} className="text-red-400" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        );
      }
    }
  ];

  const awardsColumns = [
    {
      label: (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
              checked={paginatedAwards.length > 0 && paginatedAwards.every(item => selectedAwards.includes(item.id))}
              onChange={e => {
                if (e.target.checked) {
                  const toAdd = paginatedAwards.map(i => i.id);
                  setSelectedAwards(prev => Array.from(new Set([...prev, ...toAdd])));
                } else {
                  const toRemove = paginatedAwards.map(i => i.id);
                  setSelectedAwards(prev => prev.filter(id => !toRemove.includes(id)));
                }
              }}
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
      render: (item: any) => (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
              checked={selectedAwards.includes(item.id)}
              onChange={e => {
                if (e.target.checked) {
                  setSelectedAwards(prev => [...prev, item.id]);
                } else {
                  setSelectedAwards(prev => prev.filter(id => id !== item.id));
                }
              }}
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      )
    },
    {
      label: "SR.NO",
      render: (_: any, idx: number) => (
        <span className="text-gray-400 font-bold font-mono">
          {(page - 1) * itemsPerPage + idx + 1}
        </span>
      )
    },
    {
      label: "ICON",
      render: (item: any) => {
        const opt = ICON_OPTIONS.find(o => o.key === item.iconKey) || ICON_OPTIONS[0];
        const CustomIcon = opt.component;
        return (
          <div className="flex justify-center">
            <div className={`w-8 h-8 rounded-lg ${opt.colorClass} flex items-center justify-center shadow-xs`}>
              <CustomIcon size={16} style={{ color: item.color }} />
            </div>
          </div>
        );
      }
    },
    {
      label: "AWARD",
      render: (item: any) => (
        <span className="font-bold text-gray-800">{item.title}</span>
      )
    },
    {
      label: "SUMMARY",
      render: (item: any) => (
        <span className="text-gray-500 font-medium max-w-xs truncate leading-normal block mx-auto text-center">
          {item.summary}
        </span>
      )
    },
    {
      label: "STATUS",
      render: (item: any) => (
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={() => handleToggleAwardStatus(item)}
            className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out outline-none ${
              item.active ? "bg-black" : "bg-gray-250"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                item.active ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
      )
    },
    {
      label: "CREATED AT",
      render: (item: any) => (
        <span className="text-gray-500 font-medium font-mono whitespace-nowrap">{item.createdAt}</span>
      )
    },
    {
      label: "ACTIONS",
      render: (item: any) => {
        const isOpen = activeActionMenuId === item.id;
        return (
          <div className={`relative flex justify-center ${isOpen ? "z-30" : "z-10"}`}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveActionMenuId(isOpen ? null : item.id);
              }}
              className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
              title="Actions"
            >
              <IconDotsVertical size={14} stroke={1.5} />
            </button>
            
            {isOpen && (
              <>
                <div 
                  className="fixed inset-0 z-40 bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveActionMenuId(null);
                  }}
                />
                <div className="absolute right-0 top-8 z-50 min-w-[120px] bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 text-left font-sans text-xs font-semibold">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenuId(null);
                      handleOpenView(item);
                    }}
                    className="w-full px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-slate-700 cursor-pointer text-left border-none bg-transparent"
                  >
                    <IconEye size={13} className="text-gray-400" />
                    View
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenuId(null);
                      handleOpenEdit(item);
                    }}
                    className="w-full px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2 text-slate-700 cursor-pointer text-left border-none bg-transparent"
                  >
                    <IconEdit size={13} className="text-gray-400" />
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveActionMenuId(null);
                      handleOpenDelete(item);
                    }}
                    className="w-full px-4 py-2 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-2 text-red-500 cursor-pointer text-left border-none bg-transparent"
                  >
                    <IconTrash size={13} className="text-red-400" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        );
      }
    }
  ];

  const isSidebarOpen = sidebarMode !== null;

  // Filter columns when sidebar is open to preserve layout readability
  const activeAwardsColumns = isSidebarOpen && activeTab === "awards"
    ? awardsColumns.filter((_, idx) => [1, 2, 3, 5, 7].includes(idx)) // Show SR.NO, ICON, AWARD, STATUS, ACTIONS
    : awardsColumns;

  const activeAppreciationColumns = isSidebarOpen && activeTab === "appreciation"
    ? appreciationColumns.filter((_, idx) => [1, 2, 3, 4, 7].includes(idx)) // Show SR.NO, NAME, AWARD, GIVEN ON, ACTIONS
    : appreciationColumns;

  // Sidebar Content Component for Awards
  const awardSidebarContent = sidebarMode ? (
    <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden animate-in slide-in-from-right duration-200 font-sans">
      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-[#212121]">
            {sidebarMode === "ADD"
              ? "Create Award Category"
              : sidebarMode === "VIEW"
              ? "Award Details"
              : sidebarMode === "IMPORT"
              ? "Import Awards"
              : "Update Award Type"}
          </h2>
          {sidebarMode === "VIEW" && selectedItem && (
            <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${selectedItem.active ? "bg-emerald-500 text-white" : "bg-gray-400 text-white"}`}>
              {selectedItem.active ? "Active" : "Inactive"}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setSidebarMode(null);
            setSelectedItem(null);
          }}
          className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
          title="Close"
        >
          <IconX size={18} />
        </button>
      </div>

      {sidebarMode === "DELETE" ? (
        <div className="p-6 bg-white space-y-6">
          <p className="text-sm text-gray-600 font-medium">
            Are you sure you want to delete this Award?
          </p>
          {selectedItem && (
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 font-mono">AWARD NAME</span>
              <span className="text-sm font-semibold text-gray-805">{selectedItem.title}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 font-bold font-sans">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-650 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDeleteAward}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FF0000] hover:bg-red-700 text-white text-xs font-semibold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      ) : sidebarMode === "VIEW" && selectedItem ? (
        <div className="p-6 bg-white space-y-6 max-h-[calc(100vh-160px)] overflow-y-auto font-sans">
          <div className="border border-gray-150 rounded-2xl p-5 bg-white space-y-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 border-b border-gray-100 pb-2">Basic Info</h3>
            <div className="grid grid-cols-3 gap-4 text-left">
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase block">Title</span>
                <span className="text-xs font-bold text-gray-800 leading-tight block mt-1">{selectedItem.title}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase block">Icon</span>
                <div className="mt-1">
                  <div className={`w-7 h-7 rounded-lg ${getAwardIconConfig(selectedItem.title).colorClass} flex items-center justify-center shadow-xs`}>
                    {React.createElement(getAwardIconConfig(selectedItem.title).component, { size: 14, style: { color: selectedItem.color } })}
                  </div>
                </div>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase block">Created At</span>
                <span className="text-xs font-bold text-gray-800 block mt-1 font-mono">{selectedItem.createdAt}</span>
              </div>
            </div>
            
            <div className="pt-3 text-left border-t border-gray-50">
              <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Summary</span>
              <p className="text-xs text-gray-650 font-medium leading-relaxed mt-0.5">
                {selectedItem.summary}
              </p>
            </div>
          </div>
        </div>
      ) : sidebarMode === "IMPORT" ? (
        <div className="p-6 bg-white space-y-6 max-h-[calc(100vh-160px)] overflow-y-auto text-left font-sans">
          <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/50 flex flex-col gap-1 text-xs">
            <div className="font-bold text-slate-800">
              Before uploading the sheet, please ensure that the following fields are included in the Excel file:
            </div>
            <div className="text-gray-500 font-medium mt-1">
              Make sure headers match exactly (case-insensitive). Missing fields may cause import errors.
              <span className="text-blue-500 hover:underline font-bold ml-1.5 cursor-pointer" onClick={() => toast("Required columns: Title, Category, Summary, Icon, Color, Is Reward Allowed")}>Show fields</span>
            </div>
          </div>

          <div className="p-4 rounded-xl border border-gray-150 bg-slate-50 flex items-center justify-between gap-4">
            <div className="text-xs text-left">
              <div className="font-bold text-slate-800">Need a sample file to understand the import format?</div>
              <div className="text-gray-400 font-medium mt-0.5 leading-relaxed">Download the template and upload your data in the same structure.</div>
            </div>
            <button
              onClick={() => {
                toast.success("Sample template downloaded");
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-gray-50 text-xs font-bold text-slate-700 transition cursor-pointer shrink-0 shadow-sm"
            >
              <IconDownload size={14} className="text-gray-400" />
              Sample File
            </button>
          </div>

          {/* Drag drop target */}
          <div className="border-2 border-dashed border-gray-200 hover:border-[#FBD037] transition rounded-2xl p-8 flex flex-col items-center justify-center gap-2 cursor-pointer bg-slate-50/20 relative">
            <input
              type="file"
              accept=".xlsx,.xls,.xlsm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  toast.success(`Selected file: ${file.name}`);
                }
              }}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
            <IconUpload size={28} className="text-gray-400" />
            <div className="text-xs font-bold text-slate-800 text-center">
              Click or drag an Excel file here
            </div>
            <button className="mt-1 px-3.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200/80 text-[10px] font-black uppercase text-slate-600 transition tracking-wide">
              Choose file
            </button>
            <div className="text-[10px] text-gray-400 mt-1">Allowed: .xlsx, .xls, .xlsm • Max 20 MB</div>
          </div>

          {/* Action buttons */}
          <div className="pt-4 flex justify-end gap-2.5 border-t border-slate-100 font-bold font-sans">
            <button
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setSidebarMode(null);
                toast.success("Excel editor opened");
              }}
              className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm bg-[#64748B] hover:bg-[#475569] text-white transition cursor-pointer border-none"
            >
              Open Editor
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleFormSubmit} className="bg-[#FCFDF8] p-5 max-h-[calc(100vh-160px)] overflow-y-auto space-y-4 text-left font-sans">
          <SectionBlock title="Award Category Details" icon={IconAward}>
            <div className="space-y-3.5">
              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Title *</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                  placeholder="Ex. Employee Of The Month"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Category *</label>
                <input
                  type="text"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  placeholder="Ex. Performance"
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Icon *</label>
                <select
                  value={editIconKey}
                  onChange={(e) => setEditIconKey(e.target.value)}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                >
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.key.charAt(0).toUpperCase() + opt.key.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Select a Color *</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="w-full rounded-xl py-[10px] px-3 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                    />
                    <input
                      type="color"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="w-10 h-10 rounded-xl cursor-pointer border border-[#D8D8D8] bg-transparent flex-shrink-0 p-0.5"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Is Reward Allowed *</label>
                  <div className="flex items-center gap-3 bg-slate-50 p-[7px] rounded-xl border border-[#D8D8D8] h-[38px] justify-around">
                    {[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" }
                    ].map((option) => {
                      const isSelected = editRewardAllowed === option.value;
                      return (
                        <label
                          key={option.label}
                          className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-all cursor-pointer select-none ${
                            isSelected
                              ? "bg-[#FBD037]/15 border-[#FBD037] text-[#212121]"
                              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="rewardAllowed"
                            checked={isSelected}
                            onChange={() => setEditRewardAllowed(option.value)}
                            className="hidden"
                          />
                          <span className={`w-1.5 h-1.5 rounded-full transition-all ${isSelected ? "bg-[#212121]" : "bg-slate-300"}`} />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </SectionBlock>

          <SectionBlock title="Description Summary" icon={IconFileText}>
            <div className="space-y-1">
              <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Summary *</label>
              <textarea
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
                placeholder="Ex. Awarded to employees who deliver exceptional performance throughout the month."
                rows={4}
                className="w-full rounded-xl py-2 px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold resize-none"
                required
              />
            </div>
          </SectionBlock>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 font-bold">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-5 py-2 border border-gray-200 rounded-xl text-xs font-semibold hover:bg-gray-50 text-gray-700 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] rounded-xl text-xs font-bold transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150"
            >
              {isSubmitting ? (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#212121]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : null}
              <span>{sidebarMode === "ADD" ? "Create Category" : "Save"}</span>
            </button>
          </div>
        </form>
      )}
    </div>
  ) : null;

  // Sidebar Content Component for Appreciations
  const appreciationSidebarContent = sidebarMode ? (
    <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden animate-in slide-in-from-right duration-200 font-sans">
      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-2 text-left">
          <h2 className="text-lg font-bold text-[#212121]">
            {sidebarMode === "ADD"
              ? "Assign Appreciation"
              : sidebarMode === "VIEW"
              ? "Appreciation Details"
              : sidebarMode === "IMPORT"
              ? "Import Data"
              : sidebarMode === "EDIT"
              ? "Update Appreciation"
              : "Delete Appreciation"}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => {
            setSidebarMode(null);
            setSelectedItem(null);
          }}
          className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
          title="Close"
        >
          <IconX size={18} />
        </button>
      </div>

      {sidebarMode === "DELETE" ? (
        <div className="p-6 bg-white space-y-6 text-left">
          <p className="text-sm text-gray-600 font-medium">
            Are you sure you want to delete this appreciation record?
          </p>
          {selectedItem && (
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 font-mono">EMPLOYEE</span>
              <span className="text-sm font-bold text-gray-800">{selectedItem.name}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 font-bold">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-655 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDeleteAppreciation}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FF0000] hover:bg-red-700 text-white text-xs font-semibold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      ) : sidebarMode === "IMPORT" ? (
        <div className="p-6 bg-white space-y-6 max-h-[calc(100vh-160px)] overflow-y-auto text-left">
          <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/50 flex flex-col gap-1 text-xs">
            <div className="font-bold text-slate-800">
              Before uploading the sheet, please ensure that the following fields are included in the Excel file:
            </div>
            <div className="text-gray-500 font-medium mt-1">
              Make sure headers match exactly (case-insensitive). Missing fields may cause import errors.
              <span className="text-blue-500 hover:underline font-bold ml-1.5 cursor-pointer" onClick={() => toast("Required columns: Employee ID, Award ID, Recognized By ID, Recognition Date, Visibility, Summary")}>Show fields</span>
            </div>
          </div>

          <div className="p-4 rounded-xl border border-gray-150 bg-slate-50 flex items-center justify-between gap-4">
            <div className="text-xs text-left">
              <div className="font-bold text-slate-800">Need a sample file to understand the import format?</div>
              <div className="text-gray-400 font-medium mt-0.5 leading-relaxed">Download the template and upload your data in the same structure.</div>
            </div>
            <button
              onClick={() => {
                toast.success("Sample template downloaded");
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-gray-50 text-xs font-bold text-slate-700 transition cursor-pointer shrink-0 shadow-sm"
            >
              <IconDownload size={14} className="text-gray-400" />
              Sample File
            </button>
          </div>

          {/* Drag drop target */}
          <div className="border-2 border-dashed border-gray-200 hover:border-[#FBD037] transition rounded-2xl p-8 flex flex-col items-center justify-center gap-2 cursor-pointer bg-slate-50/20 relative">
            <input
              type="file"
              accept=".xlsx,.xls,.xlsm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  toast.success(`Selected file: ${file.name}`);
                }
              }}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
            <IconUpload size={28} className="text-gray-400" />
            <div className="text-xs font-bold text-slate-800 text-center">
              Click or drag an Excel file here
            </div>
            <button className="mt-1 px-3.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200/80 text-[10px] font-black uppercase text-slate-600 transition tracking-wide">
              Choose file
            </button>
            <div className="text-[10px] text-gray-400 mt-1">Allowed: .xlsx, .xls, .xlsm • Max 20 MB</div>
          </div>

          {/* Action buttons */}
          <div className="pt-4 flex justify-end gap-2.5 border-t border-slate-100 font-bold font-sans">
            <button
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 rounded-xl text-xs font-bold text-gray-700 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setSidebarMode(null);
                toast.success("Excel editor opened");
              }}
              className="px-5 py-2 rounded-xl text-xs font-bold shadow-sm bg-[#64748B] hover:bg-[#475569] text-white transition cursor-pointer border-none"
            >
              Open Editor
            </button>
          </div>
        </div>
      ) : sidebarMode === "VIEW" && selectedItem ? (
        <div className="p-6 bg-white space-y-6 max-h-[calc(100vh-160px)] overflow-y-auto text-left font-sans">
          {/* Employee Avatar + Name + Dept card */}
          <div className="border border-gray-200/60 rounded-3xl p-4 bg-white flex items-center gap-4 shadow-xs">
            {renderAvatar(selectedItem.name)}
            <div>
              <div className="text-sm font-black text-gray-800 leading-tight">{selectedItem.name}</div>
              <div className="text-xs text-gray-400 font-bold mt-0.5">{selectedItem.dept}</div>
            </div>
          </div>

          <div className="border border-gray-200/60 rounded-3xl p-5 bg-white space-y-4 shadow-sm">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider pb-1.5 border-b border-gray-50">Basic Info</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-[10px] font-bold text-gray-450 uppercase block">Award</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className={`w-7 h-7 rounded-lg ${getAwardIconConfig(selectedItem.award).colorClass} flex items-center justify-center shadow-xs`}>
                    {React.createElement(getAwardIconConfig(selectedItem.award).component, { size: 14 })}
                  </div>
                  <span className="text-xs font-bold text-gray-850">{selectedItem.award}</span>
                </div>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-450 uppercase block">Given By</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xs font-bold text-gray-850">
                    {selectedItem.recognizedBy ? `${selectedItem.recognizedBy.firstName || ""} ${selectedItem.recognizedBy.lastName || ""}`.trim() : "Unknown"}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-455 uppercase block">Given Date</span>
                <span className="text-xs font-bold text-gray-850 block mt-0.5 font-mono">{selectedItem.givenOn}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-455 uppercase block">Visibility</span>
                <span className="text-xs font-bold text-gray-850 block mt-0.5 font-mono uppercase">{selectedItem.rawVisibility}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-455 uppercase block">Created At</span>
                <span className="text-xs font-semibold text-gray-850 block mt-0.5 font-mono">{selectedItem.createdAt}</span>
              </div>
            </div>
            <div className="border-t border-gray-50 pt-3">
              <span className="text-[10px] font-bold text-gray-455 uppercase block mb-1">Summary</span>
              <p className="text-xs text-gray-650 font-medium leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100/50">
                {selectedItem.rawSummary}
              </p>
            </div>

            {selectedItem.rawAttachments && selectedItem.rawAttachments.length > 0 && (
              <div className="border-t border-gray-50 pt-3">
                <span className="text-[10px] font-bold text-gray-455 uppercase block mb-1.5">Attachments</span>
                <div className="flex flex-wrap gap-2">
                  {selectedItem.rawAttachments.map((att: any, idx: number) => {
                    const fileUrl = typeof att === "string" ? att : att.url || "";
                    const fileName = typeof att === "string" ? att.substring(att.lastIndexOf("/") + 1) : att.name || `file_${idx}`;
                    return (
                      <a
                        key={idx}
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[10px] font-bold text-slate-700 transition cursor-pointer"
                      >
                        <IconDownload size={12} className="text-gray-450" />
                        <span className="max-w-[120px] truncate">{fileName}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={handleAppreciationSubmit} className="bg-[#FCFDF8] p-5 max-h-[calc(100vh-160px)] overflow-y-auto space-y-4 text-left font-sans text-gray-700">
          <SectionBlock title="Appreciation Details" icon={IconTrophy}>
            <div className="space-y-3.5">
              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Employee *</label>
                <select
                  value={newAppEmployee}
                  onChange={(e) => setNewAppEmployee(e.target.value)}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Award Type *</label>
                <div className="flex gap-2">
                  <select
                    value={newAppAward}
                    onChange={(e) => setNewAppAward(e.target.value)}
                    className="w-full flex-1 rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                    required
                  >
                    <option value="">Select Award</option>
                    {allAwards.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setModalAwardTitle("");
                      setModalAwardCategory("");
                      setModalAwardIconKey("");
                      setModalAwardColor("#ff0000");
                      setModalAwardRewardAllowed(false);
                      setModalAwardSummary("");
                      setShowCreateAwardModal(true);
                    }}
                    className="px-3.5 bg-black hover:bg-slate-900 text-[#FBD037] hover:text-[#ffd644] rounded-xl flex items-center justify-center font-bold text-sm shadow-sm transition-all cursor-pointer border-none"
                    title="Create Award Type"
                  >
                    <IconPlus size={16} stroke={3} />
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Recognized by *</label>
                <select
                  value={newAppRecognizedBy}
                  onChange={(e) => setNewAppRecognizedBy(e.target.value)}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                  required
                >
                  <option value="">Select Recognizer</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Given On Date *</label>
                  <input
                    type="date"
                    value={newAppGivenOn}
                    onChange={(e) => setNewAppGivenOn(e.target.value)}
                    className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Visibility *</label>
                  <select
                    value={newAppVisibility}
                    onChange={(e) => setNewAppVisibility(e.target.value)}
                    className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                    required
                  >
                    <option value="PUBLIC">Public</option>
                    <option value="PRIVATE">Private</option>
                  </select>
                </div>
              </div>
            </div>
          </SectionBlock>

          <SectionBlock title="Supporting Materials" icon={IconFileText}>
            <div className="space-y-3.5">
              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Summary *</label>
                <textarea
                  value={newAppSummary}
                  onChange={(e) => setNewAppSummary(e.target.value)}
                  placeholder="Enter summary..."
                  rows={4}
                  className="w-full rounded-xl py-2 px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold resize-none"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">Attachment</label>
                <div className="border-2 border-dashed border-gray-200 hover:border-[#FBD037] transition rounded-2xl p-4 flex flex-col items-center justify-center gap-1 cursor-pointer bg-slate-50/10 relative">
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setNewAppFile(file);
                        setNewAppFileName(file.name);
                      }
                    }}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <IconUpload size={20} className="text-gray-400" />
                  <span className="text-xs font-bold text-slate-700 text-center leading-normal">
                    {newAppFileName || "Drag & drop or click to browse"}
                  </span>
                  <span className="text-[9px] text-gray-400 mt-0.5">Maximum 1 file • Max 3MB</span>
                </div>
              </div>
            </div>
          </SectionBlock>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 font-bold">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-5 py-2 border border-gray-200 rounded-xl text-xs font-semibold hover:bg-gray-50 text-gray-700 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] rounded-xl text-xs font-bold transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150"
            >
              {isSubmitting ? (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#212121]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : null}
              <span>{sidebarMode === "ADD" ? "Save Assignment" : "Save Changes"}</span>
            </button>
          </div>
        </form>
      )}
    </div>
  ) : null;

  return (
    <div className="w-full mx-auto mt-2 animate-in fade-in duration-300 relative">
      
      {/* Sub tabs of appreciation top of table */}
      <div className="flex justify-start mb-4 px-2">
        <div className="flex bg-[#E4E7EB] rounded-xl p-1 font-sans text-xs font-bold shadow-inner">
          <button
            onClick={() => {
              setActiveTab("appreciation");
              setPage(1);
              setSearchQuery("");
              setSidebarMode(null);
              setSelectedItem(null);
            }}
            className={`px-4 py-1.5 rounded-lg transition-all cursor-pointer ${
              activeTab === "appreciation"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Appreciation
          </button>
          <button
            onClick={() => {
              setActiveTab("awards");
              setPage(1);
              setSearchQuery("");
              setSidebarMode(null);
              setSelectedItem(null);
            }}
            className={`px-4 py-1.5 rounded-lg transition-all cursor-pointer ${
              activeTab === "awards"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Awards
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-b-3xl rounded-tr-3xl border border-white/20 shadow-sm overflow-hidden mb-6">
        {/* Toolbar Controls */}
        <div className="px-6 py-1.5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-50 bg-slate-50/10">
          
          {/* Entries count selection */}
          <div className="flex items-center gap-2">
            <select
              value={itemsPerPage}
              disabled
              className="border border-slate-200 rounded-lg px-2 py-1 text-sm bg-white outline-none focus:ring-1 focus:ring-[#FBD037] h-8 cursor-not-allowed"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500 mr-2">entries</span>
          </div>

          <div className="flex items-center flex-wrap gap-2.5">
            {/* Delete button (if items are selected) */}
            {activeTab === "appreciation" && selectedAppreciations.length > 0 && (
              <button
                onClick={handleDeleteAppreciations}
                className="bg-red-55 text-red-600 border border-red-200 px-3 h-8 rounded-lg flex items-center gap-1.5 text-xs font-bold hover:bg-red-100 transition shadow-sm mr-2 cursor-pointer border-none"
              >
                <IconTrash size={14} /> Delete ({selectedAppreciations.length})
              </button>
            )}
            {activeTab === "awards" && selectedAwards.length > 0 && (
              <button
                onClick={handleDeleteAwards}
                className="bg-red-55 text-red-600 border border-[#FFD2D2] px-3 h-8 rounded-lg flex items-center gap-1.5 text-xs font-bold hover:bg-red-100 transition shadow-sm mr-2 cursor-pointer border-none"
              >
                <IconTrash size={14} /> Delete ({selectedAwards.length})
              </button>
            )}

            {/* Import / Export actions */}
            <button
              onClick={() => {
                if (activeTab === "appreciation") {
                  handleOpenImportAppreciation();
                } else {
                  setSelectedItem(null);
                  setSidebarMode("IMPORT");
                }
              }}
              className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 h-8 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition shadow-sm cursor-pointer"
            >
              <IconUpload size={14} /> Import
            </button>
            <button
              onClick={() => {
                toast.success("Export started");
              }}
              className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 h-8 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition shadow-sm cursor-pointer"
            >
              <IconDownload size={14} /> Export
            </button>

            {/* Search Input Box */}
            <div className="flex items-center gap-2 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconSearch size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={activeTab === "appreciation" ? "Search..." : "Search awards..."}
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-9 pr-4 h-8 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:ring-1 focus:ring-[#FBD037] transition"
              />
            </div>

            {/* Refresh button */}
            <button
              onClick={() => {
                if (activeTab === "appreciation") loadAppreciations();
                else loadAwards();
              }}
              className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
              title="Refresh"
            >
              <IconRefresh size={17} stroke={2.5} />
            </button>

            {/* Add button inside toolbar */}
            <button
              onClick={() => {
                if (activeTab === "appreciation") {
                  handleOpenAddAppreciation();
                } else {
                  handleOpenAdd();
                }
              }}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer border-none"
              title={activeTab === "appreciation" ? "Add Appreciation" : "Add Award"}
            >
              <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
            </button>
          </div>
        </div>

        {/* Main Layout Grid */}
        <div className="grid grid-cols-12 gap-6 p-2 items-start w-full">
          <div className={isSidebarOpen ? "col-span-12 xl:col-span-7 transition-all duration-300" : "col-span-12"}>
            {/* Table Content */}
            <div className="pb-2">
              {isLoading ? (
                <div className="p-8 flex justify-center items-center">
                  <svg
                    className="animate-spin h-6 w-6 text-[#FBD037]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                </div>
              ) : error ? (
                <div className="p-8 text-center text-red-500 font-semibold">{error}</div>
              ) : activeTab === "awards" && awards.length === 0 ? (
                <div className="p-8 text-center text-gray-400 font-medium">No Award Categories Found</div>
              ) : activeTab === "appreciation" && appreciations.length === 0 ? (
                <div className="p-8 text-center text-gray-400 font-medium">No Appreciation Records Found</div>
              ) : (
                <>
                  {activeTab === "appreciation" && (
                    <ListView
                      columns={activeAppreciationColumns}
                      data={paginatedAppreciations}
                      trClassName="hover:bg-[#FBD037]/10 group transition-colors duration-150 border-b border-gray-100 cursor-pointer"
                      mainDivClassName="bg-[#F7F6F2] rounded-3xl"
                      mailDivClassName="custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner max-h-[75vh] min-h-[250px]"
                      scrollbarClassName="custom-scrollbar"
                      thClassName="text-[11px] font-semibold tracking-wider uppercase select-none text-[#131327]/70"
                    />
                  )}
                  {activeTab === "awards" && (
                    <ListView
                      columns={activeAwardsColumns}
                      data={paginatedAwards}
                      trClassName="hover:bg-[#FBD037]/10 group transition-colors duration-150 border-b border-gray-100 cursor-pointer"
                      mainDivClassName="bg-[#F7F6F2] rounded-3xl"
                      mailDivClassName="custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner max-h-[75vh] min-h-[250px]"
                      scrollbarClassName="custom-scrollbar"
                      thClassName="text-[11px] font-semibold tracking-wider uppercase select-none text-[#131327]/70"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Desktop Sidebar (static column inside grid) */}
          {isSidebarOpen && (
            <div className="hidden xl:block xl:col-span-5 space-y-4 mt-2">
              {activeTab === "appreciation" ? appreciationSidebarContent : awardSidebarContent}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {displayTotalPages > 1 && (
          <div className="py-4 flex justify-center">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(p => Math.max(p - 1, 1))}
                disabled={page === 1}
                className="p-1 rounded-md border text-gray-400 hover:bg-gray-50 disabled:opacity-50"
              >
                <IconChevronLeft size={20} />
              </button>
              {Array.from({ length: displayTotalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-3 py-1 rounded-md text-sm font-medium shadow-sm transition ${
                    page === i + 1
                      ? "bg-[#FBD037] text-[#212121]"
                      : "bg-white border text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(p + 1, displayTotalPages))}
                disabled={page === displayTotalPages}
                className="p-1 rounded-md border text-gray-400 hover:bg-gray-50 disabled:opacity-50"
              >
                <IconChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile/Tablet Sidebar (fixed overlay outside grid) */}
      {isSidebarOpen && (
        <div className="xl:hidden">
          <div
            className="fixed inset-0 bg-black/40 z-30"
            onClick={() => {
              setSidebarMode(null);
              setSelectedItem(null);
            }}
          />
          <div className="fixed inset-y-0 right-0 z-[35] w-full sm:w-[500px] p-4 pt-[76px] sm:pt-[96px] shadow-2xl bg-[#F7F6F2] overflow-y-auto flex flex-col justify-start">
            {activeTab === "appreciation" ? appreciationSidebarContent : awardSidebarContent}
          </div>
        </div>
      )}


      {/* Create Award Type Modal */}
      {showCreateAwardModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateAwardModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl z-10">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#212121]">Create Award Type</h2>
              <button
                type="button"
                onClick={() => setShowCreateAwardModal(false)}
                className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
              >
                <IconX size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleCreateAwardFromModal} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold block">Title *</label>
                  <input
                    type="text"
                    value={modalAwardTitle}
                    onChange={(e) => setModalAwardTitle(e.target.value)}
                    placeholder="Ex. Discipline"
                    className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold block">Catagory *</label>
                  <input
                    type="text"
                    value={modalAwardCategory}
                    onChange={(e) => setModalAwardCategory(e.target.value)}
                    placeholder="Ex. Perfomance"
                    className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold block">Icon *</label>
                <select
                  value={modalAwardIconKey}
                  onChange={(e) => setModalAwardIconKey(e.target.value)}
                  className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold cursor-pointer"
                  required
                >
                  <option value="">Select Icon</option>
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.key.charAt(0).toUpperCase() + opt.key.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold block">Select a Color *</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={modalAwardColor}
                      onChange={(e) => setModalAwardColor(e.target.value)}
                      className="w-full rounded-xl py-[10px] px-3 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                    />
                    <input
                      type="color"
                      value={modalAwardColor}
                      onChange={(e) => setModalAwardColor(e.target.value)}
                      className="w-10 h-10 rounded-xl cursor-pointer border border-[#D8D8D8] bg-transparent flex-shrink-0 p-0.5"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[#1E293B] text-xs font-semibold block">Is Reward Allowed *</label>
                  <div className="flex items-center gap-3 bg-slate-50 p-[7px] rounded-xl border border-[#D8D8D8] h-[38px] justify-around">
                    {[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" }
                    ].map((option) => {
                      const isSelected = modalAwardRewardAllowed === option.value;
                      return (
                        <label
                          key={option.label}
                          className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-all cursor-pointer select-none ${
                            isSelected
                              ? "bg-[#FBD037]/15 border-[#FBD037] text-[#212121]"
                              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="modalRewardAllowed"
                            checked={isSelected}
                            onChange={() => setModalAwardRewardAllowed(option.value)}
                            className="hidden"
                          />
                          <span className={`w-1.5 h-1.5 rounded-full transition-all ${isSelected ? "bg-[#212121]" : "bg-slate-300"}`} />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[#1E293B] text-xs font-semibold block">Summary *</label>
                <textarea
                  value={modalAwardSummary}
                  onChange={(e) => setModalAwardSummary(e.target.value)}
                  placeholder="Enter your summary"
                  rows={3}
                  className="w-full rounded-xl py-2 px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold resize-vertical"
                  required
                />
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCreateAwardModal(false)}
                  className="px-5 py-2 border border-gray-200 rounded-xl text-xs font-semibold hover:bg-gray-50 text-gray-700 transition cursor-pointer bg-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-[#212121] text-white hover:bg-[#333] rounded-xl text-xs font-bold transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm"
                >
                  {isSubmitting ? (
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : null}
                  <span>Save</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
